import { useAsync, UseAsyncStatus } from '@shared/async';
import { useErrorHandler } from '@shared/errors';
import { useHeaderDetails } from '@shared/header';
import React, { useEffect, useMemo, useState } from 'react';
import {
  HiOutlineArchiveBox,
  HiOutlineCalendar,
  HiOutlineChartBar,
  HiOutlineCheckCircle,
  HiOutlineClock,
  HiOutlineCurrencyDollar,
  HiOutlinePaperClip,
  HiOutlinePencil,
  HiOutlinePencilSquare,
  HiOutlineUser,
  HiOutlineWrenchScrewdriver,
} from 'react-icons/hi2';
import { useNavigate, useParams } from 'react-router-dom';

import { AttachmentUploadDialog } from '@/components/attachment-upload-dialog';
import { AttachmentsTable } from '@/components/attachments-table';
import { Alert, AlertBody, AlertTitle } from '@/components-new/alert';
import { Badge } from '@/components-new/badge';
import { Button } from '@/components-new/button';
import { PageTitleRow } from '@/components-new/page-title-row';
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@/components-new/tab';
import { TextLink } from '@/components-new/text';
import { MarketBasket, useMarketBasketService } from '@/features/market-baskets';
import { MarketBasketBidAnalysis } from '@/features/market-baskets/api/use-market-baskets-service';
import { MarketBasketDrugPackaging } from '@/features/market-baskets/types/market-basket';
import { ChangeLogTable } from '@/features/market-baskets/components/show/change-log';
import { DrugMarketDetails } from '@/features/market-baskets/components/show/drug-market-details';
import { useMarketBasketDetails } from '@/features/market-baskets/market-basket-details.provider';
import { notifySuccess } from '@/lib/notification/notifications';
import { formatCurrency, formatPercent } from '@/utils/formatting';
import { formatTimestamp, formatYearMonth } from '@/utils/date-time-functions';
import MarketBasketPurposeBadge from '@/features/market-baskets/components/market-basket-purpose-badge';

// ─── helpers ────────────────────────────────────────────────────────────────
const hasOverride = (p: MarketBasketDrugPackaging) =>
  p.currentWacPrice?.overrideValue != null ||
  p.currentWacUnitPrice?.overrideValue != null ||
  p.amp?.overrideValue != null ||
  p.totalUra?.overrideValue != null ||
  p.uroa?.overrideValue != null;

const hasWac = (p: MarketBasketDrugPackaging) =>
  (p.currentWacPrice?.overrideValue ?? p.currentWacPrice?.compendiumValue) != null;

const effectiveWac = (p: MarketBasketDrugPackaging) =>
  p.currentWacPrice?.overrideValue ?? p.currentWacPrice?.compendiumValue ?? 0;

const effectiveUraPercent = (p: MarketBasketDrugPackaging) =>
  p.uraPercentWac?.proprietaryValue?.value ?? p.uraPercentWac?.calculatedValue?.value;

type AlertMessages = {
  warnings: Array<{ title: string; body: string }>;
  errors: Array<{ title: string; body: string }>;
};

const buildAlerts = (mb?: MarketBasket): AlertMessages => {
  const warnings: AlertMessages['warnings'] = [];
  const errors: AlertMessages['errors'] = [];
  if (!mb) return { warnings, errors };

  const pkgs = mb.drugPackagings ?? [];

  if (pkgs.length === 0) {
    warnings.push({ title: 'No drug packagings', body: 'This market basket has no drug packagings yet.' });
  }

  if (mb.purpose === 'market_share' && mb.drug?.id) {
    const exists = pkgs.some(p => p.drug?.id === mb.drug?.id);
    if (!exists) {
      errors.push({
        title: 'Market share drug not found',
        body: `${mb.drug.label} is not in the drug packagings. This basket will not be used for market share analysis.`,
      });
    }
  }

  if (pkgs.length > 0) {
    if (pkgs.every(p => p.clientId !== mb.client?.id)) {
      warnings.push({ title: 'No client packagings', body: `None of the packagings belong to ${mb.client?.label}.` });
    }
    const missingWac = pkgs.filter(p => !hasWac(p)).length;
    if (missingWac > 0) {
      warnings.push({ title: 'Missing WAC data', body: `${missingWac} packaging${missingWac === 1 ? ' is' : 's are'} missing WAC prices and will be excluded from calculations.` });
    }
  }

  return { warnings, errors };
};

// ─── page ───────────────────────────────────────────────────────────────────

const ShowMarketBasketPage = () => {
  const { handleError } = useErrorHandler();
  const { setHeaderDetails, clearHeaderDetails } = useHeaderDetails();
  const { loadMarketBasket, marketBasket } = useMarketBasketDetails();
  const { downloadAttachment, uploadAttachment, getBidAnalysesForMarketBasket } = useMarketBasketService();
  const [bidAnalyses, setBidAnalyses] = useState<MarketBasketBidAnalysis[]>([]);
  const [loadingBidAnalyses, setLoadingBidAnalyses] = useState(false);
  const navigate = useNavigate();
  const { id } = useParams();

  const pkgs = marketBasket?.drugPackagings ?? [];
  const packagingCount = pkgs.length;
  const brandCount = pkgs.filter(p => p.brandGenericStatus === 'Brand').length;
  const genericCount = pkgs.filter(p => p.brandGenericStatus === 'Generic').length;
  const agCount = pkgs.filter(p => p.licenseType === 'AG').length;
  const attentionCount = pkgs.filter(p => p.needsAttention).length;
  const overrideCount = pkgs.filter(hasOverride).length;
  const withWac = pkgs.filter(hasWac);
  const missingWacCount = packagingCount - withWac.length;
  const totalWac = withWac.reduce((sum, p) => sum + effectiveWac(p), 0);
  const uraValues = pkgs.map(effectiveUraPercent).filter((v): v is number => v != null);
  const avgUra = uraValues.length > 0 ? uraValues.reduce((a, b) => a + b, 0) / uraValues.length : null;
  const isArchived = !!marketBasket?.archivedAt;

  const alerts = useMemo(() => buildAlerts(marketBasket), [marketBasket]);
  const hasAlerts = alerts.errors.length > 0 || alerts.warnings.length > 0;

  useEffect(() => {
    const breadcrumbs = marketBasket ? [
      { icon: HiOutlineArchiveBox, label: 'Market Baskets', url: '/market-baskets' },
      { label: marketBasket.name, url: `/market-baskets/${marketBasket.id}` },
    ] : [];

    setHeaderDetails({
      documentTitle: 'Market Basket Details',
      pageTitle: (
        <PageTitleRow title={marketBasket?.name}>
          {isArchived && <Badge color="amber">Archived</Badge>}
          <Button aria-label="Edit Market Basket" onClick={() => navigate('edit')}>
            <HiOutlinePencil/>
            Edit
          </Button>
        </PageTitleRow>
      ),
      breadcrumbs,
    });

    return () => { clearHeaderDetails(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marketBasket]);

  useEffect(() => {
    if (!id) return;
    const intId = parseInt(id);
    loadMarketBasket(intId);
    setLoadingBidAnalyses(true);
    getBidAnalysesForMarketBasket(intId)
      .then(setBidAnalyses)
      .catch(() => setBidAnalyses([]))
      .finally(() => setLoadingBidAnalyses(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const onDownload = async (url: string) => {
    try {
      const attachment = await downloadAttachment(url);
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(attachment.contents);
      link.download = attachment.filename;
      link.click();
    } catch (ex) {
      handleError(ex as Error, {
        title: 'Download Failed',
        message: 'We encountered an unexpected error while downloading your file. Please try again or contact an administrator.',
      });
    }
  };

  const [showUploadAttachmentDialog, setShowUploadAttachmentDialog] = useState(false);
  const uploadAttachmentAsync = useAsync(uploadAttachment);
  const uploading = uploadAttachmentAsync.status === UseAsyncStatus.Pending;

  useEffect(() => {
    if (uploadAttachmentAsync.status === UseAsyncStatus.Success) {
      notifySuccess({ title: 'Upload Successful', message: 'Successfully uploaded attachment.' });
      // @ts-expect-error TS(2345)
      loadMarketBasket(parseInt(id));
      setShowUploadAttachmentDialog(false);
    }
    if (uploadAttachmentAsync.status === UseAsyncStatus.Error) {
      handleError(uploadAttachmentAsync.error, { title: 'Upload Failed', message: 'Unable to upload attachment.' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadAttachmentAsync.status]);

  const handleSubmitAttachment = (file: File) => {
    // @ts-expect-error TS(2532)
    void uploadAttachmentAsync.execute(file, marketBasket.id);
  };

  return (
    <>
      <div className="mt-4 flex flex-col gap-5 pb-8">

        {/* Archive banner */}
        {isArchived && (
          <Alert variant="warning">
            <AlertTitle>This market basket is archived</AlertTitle>
            <AlertBody>
              Archived on {formatTimestamp(marketBasket!.archivedAt)}. It is read-only and excluded from calculations.
            </AlertBody>
          </Alert>
        )}

        {/* ── Bento grid ── */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">

          {/* Details card — spans 2 cols */}
          <div className="lg:col-span-2 flex flex-col rounded-2xl bg-white shadow-sm ring-1 ring-zinc-950/5 overflow-hidden">
            <div className="p-6">
              <h2 className="text-xs font-semibold uppercase tracking-widest text-zinc-400">Details</h2>
              <div className="mt-4 grid grid-cols-1 gap-x-8 gap-y-4 text-sm sm:grid-cols-2">

                <MetaRow icon={<HiOutlineUser className="size-4 text-zinc-400"/>} label="Client">
                  {marketBasket?.client?.label ?? '—'}
                </MetaRow>

                <MetaRow icon={<HiOutlineChartBar className="size-4 text-zinc-400"/>} label="Purpose">
                  {marketBasket?.purpose && <MarketBasketPurposeBadge purpose={marketBasket.purpose} />}
                </MetaRow>

                {marketBasket?.drug?.label && (
                  <div className="sm:col-span-2">
                    <MetaRow icon={<HiOutlineArchiveBox className="size-4 text-zinc-400"/>} label="Market Share Drug">
                      {marketBasket.drug.label}
                    </MetaRow>
                  </div>
                )}

                <MetaRow icon={<HiOutlineClock className="size-4 text-zinc-400"/>} label="Last Refreshed">
                  {marketBasket?.lastRefreshedAt ? formatTimestamp(marketBasket.lastRefreshedAt) : '—'}
                </MetaRow>

                <MetaRow icon={<HiOutlineCalendar className="size-4 text-zinc-400"/>} label={
                  <TextLink
                    href="https://www.bls.gov/regions/mid-atlantic/data/consumerpriceindexhistorical_us_table.htm"
                    target="_blank"
                    className="text-sm font-medium text-zinc-500 underline-offset-2 hover:underline"
                  >
                    Current CPI-U
                  </TextLink>
                }>
                  {marketBasket?.currentCpiu != null ? (
                    <span>
                      {marketBasket.currentCpiu}
                      {marketBasket.currentCpiuDate && (
                        <span className="ml-1 text-xs font-normal text-zinc-500">
                          (as of {formatYearMonth(marketBasket.currentCpiuDate)})
                        </span>
                      )}
                    </span>
                  ) : '—'}
                </MetaRow>
              </div>
            </div>

            {/* Financial + data quality strip */}
            {packagingCount > 0 && (
              <div className="mt-auto border-t border-zinc-100 bg-zinc-50/60 px-6 py-4">
                <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-zinc-400">Financial snapshot</p>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <SnapshotStat
                    icon={<HiOutlineCurrencyDollar className="size-4"/>}
                    label="Total WAC"
                    value={totalWac > 0 ? formatCurrency(totalWac, 0, 0) : '—'}
                    sub={`${withWac.length} of ${packagingCount} packagings`}
                  />
                  <SnapshotStat
                    icon={<HiOutlineChartBar className="size-4"/>}
                    label="Avg URA %"
                    value={avgUra != null ? formatPercent(avgUra, 1, 1) : '—'}
                    sub="across all packagings"
                  />
                  <SnapshotStat
                    icon={<HiOutlinePencilSquare className="size-4"/>}
                    label="Overrides"
                    value={String(overrideCount)}
                    sub={overrideCount > 0 ? 'manual value(s) active' : 'all compendium values'}
                    accent={overrideCount > 0 ? 'amber' : 'green'}
                  />
                  <SnapshotStat
                    icon={<HiOutlineWrenchScrewdriver className="size-4"/>}
                    label="Missing WAC"
                    value={String(missingWacCount)}
                    sub={missingWacCount > 0 ? 'excluded from calcs' : 'all packagings covered'}
                    accent={missingWacCount > 0 ? 'red' : 'green'}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Right column */}
          <div className="flex flex-col gap-4">

            {/* Composition tiles */}
            <div className="rounded-2xl bg-white shadow-sm ring-1 ring-zinc-950/5 overflow-hidden">
              <div className="px-5 pt-4 pb-1">
                <p className="text-xs font-semibold uppercase tracking-widest text-zinc-400">Composition</p>
              </div>
              <div className="grid grid-cols-3 divide-x divide-zinc-100 border-t border-zinc-100 mt-3">
                <CompositionTile label="Total" value={packagingCount} />
                <CompositionTile label="Brand" value={brandCount} />
                <CompositionTile label="Generic" value={genericCount} />
              </div>
              {agCount > 0 && (
                <div className="border-t border-zinc-100 px-5 py-2.5">
                  <span className="text-xs text-zinc-500">
                    <span className="font-semibold text-zinc-700">{agCount}</span> authorized generic{agCount !== 1 ? 's' : ''}
                  </span>
                </div>
              )}
            </div>

            {/* Attention alert */}
            {attentionCount > 0 && (
              <Alert variant="warning">
                <AlertTitle>Needs attention</AlertTitle>
                <AlertBody>{attentionCount} packaging{attentionCount !== 1 ? 's' : ''} need{attentionCount === 1 ? 's' : ''} attention.</AlertBody>
              </Alert>
            )}

            {/* Alerts */}
            {hasAlerts && (
              <div className="flex flex-col gap-2">
                {alerts.errors.map((msg, i) => (
                  <Alert key={i} variant="critical">
                    <AlertTitle>{msg.title}</AlertTitle>
                    <AlertBody>{msg.body}</AlertBody>
                  </Alert>
                ))}
                {alerts.warnings.map((msg, i) => (
                  <Alert key={i} variant="warning">
                    <AlertTitle>{msg.title}</AlertTitle>
                    <AlertBody>{msg.body}</AlertBody>
                  </Alert>
                ))}
              </div>
            )}

            {/* All-clear when no alerts */}
            {!hasAlerts && packagingCount > 0 && (
              <div className="flex items-center gap-2.5 rounded-2xl bg-green-50 px-5 py-3.5 ring-1 ring-green-200">
                <HiOutlineCheckCircle className="size-5 shrink-0 text-green-500"/>
                <p className="text-sm font-medium text-green-700">Basket looks healthy</p>
              </div>
            )}
          </div>
        </div>

        {/* Tabs */}
        <TabGroup>
          <TabList>
            <Tab>Drug Market Details</Tab>
            <Tab>
              Attachments
              {(marketBasket?.attachments?.length ?? 0) > 0 && (
                <Badge color="zinc" className="ml-1">{marketBasket!.attachments!.length}</Badge>
              )}
            </Tab>
            <Tab>
              Bid Analyses
              {bidAnalyses.length > 0 && (
                <Badge color="zinc" className="ml-1">{bidAnalyses.length}</Badge>
              )}
            </Tab>
            <Tab>
              Changelog
              {(marketBasket?.changelogs?.length ?? 0) > 0 && (
                <Badge color="zinc" className="ml-1">{marketBasket!.changelogs!.length}</Badge>
              )}
            </Tab>
          </TabList>
          <TabPanels>
            <TabPanel>
              <DrugMarketDetails/>
            </TabPanel>
            <TabPanel>
              <div className="flex flex-col gap-4 pt-4">
                <AttachmentsTable attachments={marketBasket?.attachments ?? []} onDownloadHandler={onDownload}/>
                <div className="flex justify-end">
                  <Button onClick={() => setShowUploadAttachmentDialog(true)}>
                    <HiOutlinePaperClip/>
                    Upload Attachment
                  </Button>
                </div>
              </div>
            </TabPanel>
            <TabPanel>
              <div className="pt-4">
                {loadingBidAnalyses ? (
                  <p className="text-sm text-zinc-500">Loading…</p>
                ) : bidAnalyses.length === 0 ? (
                  <p className="text-sm text-zinc-500">This market basket is not used in any bid analyses.</p>
                ) : (
                  <div className="divide-y divide-zinc-100 rounded-xl border border-zinc-200 bg-white">
                    {bidAnalyses.map(ba => (
                      <div key={ba.id} className="flex items-center justify-between px-4 py-3">
                        <div>
                          <TextLink href={`/bid-analyses/${ba.id}-${ba.slug}`} className="font-medium text-zinc-900">
                            {ba.name}
                          </TextLink>
                          <p className="text-sm text-zinc-500">{ba.productName}</p>
                        </div>
                        <Badge color={ba.status === 'open' ? 'green' : 'zinc'}>
                          {ba.status === 'open' ? 'Open' : 'Closed'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabPanel>
            <TabPanel>
              <div className="pt-4">
                <ChangeLogTable changeLogs={marketBasket?.changelogs ?? []}/>
              </div>
            </TabPanel>
          </TabPanels>
        </TabGroup>
      </div>

      <AttachmentUploadDialog
        open={showUploadAttachmentDialog}
        submitting={uploading}
        accept=".csv,.docx,.jpeg,.jpg,.pdf,.png,.xlsx"
        onSubmit={handleSubmitAttachment}
        onClose={() => setShowUploadAttachmentDialog(false)}
      />
    </>
  );
};

// ─── sub-components ──────────────────────────────────────────────────────────

const MetaRow = ({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode;
  label: React.ReactNode;
  children: React.ReactNode;
}) => (
  <div className="flex items-start gap-3">
    <div className="mt-0.5 shrink-0">{icon}</div>
    <div>
      <p className="font-medium text-zinc-500">{label}</p>
      <div className="mt-0.5 font-semibold text-zinc-900">{children}</div>
    </div>
  </div>
);

const CompositionTile = ({ label, value }: { label: string; value: number }) => (
  <div className="flex flex-col items-center px-4 py-4">
    <p className="text-xs font-medium text-zinc-500">{label}</p>
    <p className="mt-1 text-3xl font-bold tabular-nums text-zinc-900">{value}</p>
  </div>
);

type SnapshotAccent = 'amber' | 'red' | 'green' | 'neutral';

const SnapshotStat = ({
  icon,
  label,
  value,
  sub,
  accent = 'neutral',
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
  accent?: SnapshotAccent;
}) => {
  const valueColor: Record<SnapshotAccent, string> = {
    amber: 'text-amber-700',
    red: 'text-red-600',
    green: 'text-green-700',
    neutral: 'text-zinc-900',
  };
  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex items-center gap-1 text-zinc-500">{icon}<span className="text-xs font-medium">{label}</span></div>
      <p className={`text-lg font-bold tabular-nums ${valueColor[accent]}`}>{value}</p>
      {sub && <p className="text-xs text-zinc-400">{sub}</p>}
    </div>
  );
};

export default ShowMarketBasketPage;
